<template>
  <div class="pricing-page">
    <div class="pricing-hero text-center mx-auto px-4">
      <h1 class="text-h4 text-md-h3 font-weight-bold mb-3">
        {{ $t(embedded ? 'profile.upgradeTitle' : 'subscriptions.pricingTitle') }}
      </h1>
      <p class="text-subtitle-1 text--secondary mx-auto pricing-hero__sub">
        {{ $t(embedded ? 'profile.upgradeSubtitle' : 'subscriptions.pricingSubtitle') }}
      </p>
    </div>

    <div class="pricing-includes mt-8 mb-2">
      <div class="caption text-center text--secondary text-uppercase font-weight-medium mb-3 letter-spacing">
        {{ $t('subscriptions.allPlansInclude') }}
      </div>
      <FeatureList />
    </div>

    <v-container class="pricing-grid pt-6">
      <v-row
        v-if="loading && !plans.length"
        justify="center"
      >
        <v-col
          v-for="n in 3"
          :key="'sk-' + n"
          cols="12"
          sm="6"
          md="4"
          lg="3"
        >
          <SubscriptionCard loading />
        </v-col>
      </v-row>
      <v-row
        v-else
        justify="center"
        align="stretch"
      >
        <v-col
          v-for="(plan, index) of plans"
          :key="plan.id || index"
          cols="12"
          sm="6"
          md="4"
          :lg="plans.length >= 4 ? 3 : 4"
          class="d-flex"
        >
          <SubscriptionCard
            class="flex-grow-1"
            :active="selectedPlanIndex === index"
            :popular="index === popularIndex"
            :loading="false"
            :plan="plan"
            :read-only="readOnly || loadingContinue"
            @select="selectedPlanIndex = index"
            @continue="onClickContinue"
            @individual="individualDialog = true"
          />
        </v-col>
      </v-row>
    </v-container>

    <v-container
      v-if="!readOnly"
      class="text-center pt-2"
    >
      <v-checkbox
        v-model="termsAccepted"
        hide-details
        class="mt-2 d-inline-flex terms-check"
        :readonly="loadingContinue"
      >
        <template #label>
          <div class="caption text-left">
            {{ $t('subscriptions.terms.IAccept') }}
            <a
              target="_blank"
              rel="noopener noreferrer"
              :href="ExternalLinks.termsAndConditions"
              @click.stop
            >{{ $t('subscriptions.terms.termsOfService') }}</a>
            {{ $t('and') }}
            <a
              target="_blank"
              rel="noopener noreferrer"
              :href="ExternalLinks.privacyPolicy"
              @click.stop
            >{{ $t('subscriptions.terms.privacyPolicy') }}</a>.
            {{ $t('subscriptions.terms.info') }}
          </div>
        </template>
      </v-checkbox>
    </v-container>

    <v-container
      v-if="readOnly && packages.length"
      class="pt-10"
    >
      <OneTimePacks :packages="packages" />
    </v-container>

    <slot />

    <v-container class="pt-10 pb-4">
      <h2 class="text-h5 font-weight-bold text-center mb-6">
        {{ $t('subscriptions.faqTitle') }}
      </h2>
      <v-expansion-panels
        accordion
        class="pricing-faq mx-auto"
      >
        <v-expansion-panel>
          <v-expansion-panel-header class="font-weight-medium">
            {{ $t('subscriptions.faqChangeQ') }}
          </v-expansion-panel-header>
          <v-expansion-panel-content class="text--secondary">
            {{ $t('subscriptions.change') }}
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel>
          <v-expansion-panel-header class="font-weight-medium">
            {{ $t('subscriptions.faqIncludedQ') }}
          </v-expansion-panel-header>
          <v-expansion-panel-content class="text--secondary">
            {{ $t('subscriptions.allPlansInclude') }}:
            {{ includedFeaturesSentence }}
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel>
          <v-expansion-panel-header class="font-weight-medium">
            {{ $t('subscriptions.faqBusinessQ') }}
          </v-expansion-panel-header>
          <v-expansion-panel-content class="text--secondary">
            {{ $t('subscriptions.individualContact') }}
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-container>

    <v-dialog
      v-model="individualDialog"
      max-width="520"
    >
      <v-card class="pa-6">
        <v-card-title class="text-h6 px-0 pt-0">
          {{ $t('subscriptions.contactSales') }}
        </v-card-title>
        <v-card-text class="px-0 pb-0 text--secondary">
          {{ $t('subscriptions.individualContact') }}
        </v-card-text>
        <v-card-actions class="px-0 pb-0">
          <v-spacer />
          <v-btn
            text
            class="text-none"
            @click="individualDialog = false"
          >
            {{ $t('close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
    import Vue from 'vue';
    import {Firestore} from '@/plugins/Firestore';
    import {Stripe} from "@/plugins/Stripe";
    import {ExternalLinks} from "@/config/ExternalLinks";
    import OneTimePacks from "@/components/subscriptions/OneTimePacks.vue";
    import {captureException} from "@/plugins/Sentry";
    import SubscriptionCard from "@/components/subscriptions/SubscriptionCard.vue";
    import {StripePrice, StripeProduct, StripeProductLike, StripeRecurringProduct} from "@/model/Stripe";
    import FeatureList from "@/components/subscriptions/FeatureList.vue";
    import { Toast } from '@/model/Toast';

    export default Vue.extend({
        name: 'SubscriptionSelection',
        components: {FeatureList, SubscriptionCard, OneTimePacks},
        props: {
            readOnly: {
                type: Boolean,
                default: false
            },
            embedded: {
                type: Boolean,
                default: false
            }
        },
        data: () => ({
            plans: [] as Array<StripeProduct | StripeProductLike>,
            packages: [] as Array<StripePrice>,
            loading: false,
            loadingContinue: false,
            selectedPlanIndex: null as number | null,
            termsAccepted: false,
            individualDialog: false,
            ExternalLinks: ExternalLinks
        }),
        computed: {
            freeAnalysesCount: function (): number {
                return this.$store.getters.maxFreeAnalyses;
            },
            selectedPlan: function (): StripeProduct | StripeProductLike | null {
                if (this.selectedPlanIndex !== null) {
                    return this.plans[this.selectedPlanIndex] || null;
                }
                return null;
            },
            selectedPlanId: function (): string | undefined {
                return this.selectedPlan?.id;
            },
            popularIndex: function (): number {
                if (this.plans.length < 2) return -1;
                let i = Math.floor(this.plans.length / 2);
                if (this.plans[i]?.id === 'free') {
                    i = Math.min(i + 1, this.plans.length - 1);
                }
                return i;
            },
            includedFeaturesSentence: function (): string {
                const keys = ['aiWriting', 'seoScore', 'instructions', 'export', 'chatGpt', 'keywordAnalysis', 'inspiration'];
                return keys.map(k => String(this.$t('subscriptions.features.' + k))).join(' · ');
            },
            freePlan: function (): StripeProductLike {
                return {
                    id: 'free',
                    name: 'FREE',
                    priceLabel: this.$t('subscriptions.free'),
                    metadata: {
                        description_en: `${this.freeAnalysesCount} free analyses monthly`,
                        description_cs: `${this.freeAnalysesCount} analýzy zdarma měsíčně`,
                        footnote_en: 'Resets on the 1st of each month',
                        footnote_cs: 'Obnoví se 1. dne každého měsíce'
                    },
                };
            },
        },
        watch: {
            '$store.getters.plan.id': function () {
                this.setSelectedPlan();
            }
        },
        created: function () {
            const pricesPromises = [] as Promise<any>[];
            this.loading = true;
            const stripePlans = [] as Array<StripeRecurringProduct>;

            Firestore.getAllActiveProducts().then((products: Array<StripeProduct>) => {
                products.forEach(product => {
                    pricesPromises.push(
                        Firestore.getActivePricesForProduct(product.id).then(prices => {
                            const price: StripePrice = prices[0];
                            if (price.type === 'one_time') {
                                product.recurring = false;
                                this.packages = prices.sort((a, b) => (a.unit_amount - b.unit_amount));
                            } else {
                                product.recurring = true;
                                price.display = (price.unit_amount / 100).toFixed(2);
                                (product as StripeRecurringProduct).price = price;
                                stripePlans.push(product as StripeRecurringProduct);
                            }
                        })
                    );
                });
                return Promise.all(pricesPromises).then(() => {
                    this.plans = [
                        this.freePlan,
                        ...(stripePlans.sort((a, b) => (a.price.unit_amount - b.price.unit_amount))),
                    ];
                    this.setSelectedPlan();
                }).catch(error => {
                    this.$store.dispatch('toasts/add', {
                        type: 'error',
                        title: 'subscriptions.plansLoadingError.title',
                    } as Toast);
                    captureException(error);
                });
            }).finally(() => {
                this.loading = false;
            });
        },
        methods: {
            onClickContinue: function (plan?: StripeProduct | StripeProductLike | null): void {
                const target = plan || this.selectedPlan;
                if (!target) {
                    return;
                }
                const index = this.plans.findIndex(p => p.id === target.id);
                if (index !== -1) {
                    this.selectedPlanIndex = index;
                }
                if (!this.termsAccepted) {
                    this.$store.dispatch('toasts/add', {
                        type: 'warning',
                        title: 'subscriptions.mustAcceptTerms',
                    } as Toast);
                    return;
                }
                if (target.id === 'free') {
                    this.$router.push('/articleCrossroad');
                    return;
                }
                if (target.name === 'BUSINESS') {
                    this.individualDialog = true;
                    return;
                }
                this.loadingContinue = true;
                const priceId = (target as StripeRecurringProduct).price?.id;
                if (!priceId) {
                    console.warn('No price in plan ' + target.name);
                    this.loadingContinue = false;
                    return;
                }

                Stripe.createStripeCheckoutSession(priceId, this.$store.getters.user.uid)
                    .then(stripeCheckoutUrl => {
                        this.loadingContinue = false;
                        window.location.assign(stripeCheckoutUrl);
                    }).catch(error => {
                        this.$store.dispatch('toasts/add', {
                            type: 'error',
                            title: 'stripeError.title',
                        } as Toast);
                        captureException(error);
                        this.loadingContinue = false;
                    });
            },
            setSelectedPlan: function (): void {
                if (!this.$store.getters.plan) {
                    return;
                }
                const planIndex = this.plans.findIndex(plan => {
                    return this.$store.getters.plan && plan.id === this.$store.getters.plan.id;
                });
                if (planIndex !== -1) {
                    this.selectedPlanIndex = planIndex;
                }
            },
        }
    });
</script>

<style lang="sass" scoped>
.pricing-hero
    max-width: 720px
    padding-top: 48px

.pricing-hero__sub
    max-width: 560px

.pricing-grid
    padding-top: 24px

    @media (min-width: 960px)
        padding-top: 36px

.letter-spacing
    letter-spacing: 0.08em

.pricing-faq
    max-width: 720px

.terms-check
    max-width: 640px
</style>
